import React, { useState, useEffect, useRef } from 'react';
import RulesForm from './RulesForm';
import { StatusMessage } from '../ui/StatusMessage';
import ActionList, { ActionItem } from '../ui/ActionsList';
import Modal from '../ui/Modal';
import { ModalContent } from '../ui/ModalContent';
import { formatDateTime } from '../../helpers/formatDateTime';
import type { RuleDetail, RuleSummary } from '../../services/ruleManagement/ruleManagement.types';
import { getRule } from '../../services/ruleManagement/ruleManagement.service';
import { listSites, listSiteTree, deleteSite } from '../../services/siteManagement/siteManagement.service';
import { lookupPostcodeAddresses, AddressLookupResult } from '../../services/address/address.service';
import type { SiteListItem, SiteTreeNode } from '../../services/siteManagement/siteManagement.types';
import { FormField } from '../ui/FormField';
import { validators } from '../../helpers/validators';
import { useFormValidation } from '../../hooks/useFormValidation';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { MAX_CHAR_LENGTH, MAX_POSTCODE_LENGTH, MIN_CHAR_LENGTH } from '../../constants/validators';
import { TOAST_ERROR_MS } from '../../constants/timeouts';
export interface RuleFormData {
    ruleName?: string;
    isActive?: boolean;
    bindPathPriority?: boolean;
    distributionGroupIds?: string;
    attachmentGroupIds?: string;
    appliesToSiteIds?: string; // Comma-separated site IDs
    filteringPaths?: string;
    filteringPriorities?: number[];
    hideRtn?: boolean;
    rtnExceptions?: string;
    isUltraCritical: boolean;
}

interface BuildingFormData extends Record<string, unknown> {
    customerName: string;
    address1: string;
    address2?: string;
    townCity: string;
    county?: string;
    country: string;
    postCode: string;
}

interface BuildingFormProps {
    onSubmit: (data: BuildingFormData, rules?: RuleFormData[] | null) => void;
    onCancel?: () => void;
    initialData?: Partial<BuildingFormData>;
    isLoading?: boolean;
    siteId?: number;
    siteHierarchyId?: number;
    existingRules?: RuleSummary[];
    existingRuleDetailsFromParent?: Record<number, RuleDetail>;
    onExistingRuleDetailsChange?: (details: Record<number, RuleDetail>) => void;
    onDeleteRule?: (ruleId: number) => void;
    onActivateRule?: (ruleId: number) => void;
    onEditRule?: (ruleId: number) => void;
    editingRuleId?: number | null;
    editingRuleData?: RuleFormData | null;
    onUpdateRule?: (ruleId: number, ruleData: RuleFormData) => void;
    onCancelEditRule?: () => void;
    onEditingRuleDataChange?: (data: RuleFormData | null) => void;
    onSiteDeleted?: (siteName: string) => void;
    id?: string;
}

const formatDeleteErrorMessage = (error: unknown, fallback = 'Unknown error'): string => {
    if (error && typeof error === 'object') {
        const errObj = error as {
            message?: unknown;
            error?: unknown;
            cshGroupNames?: unknown;
            cshGroupIds?: unknown;
            response?: {
                data?: {
                    message?: unknown;
                    error?: unknown;
                    cshGroupNames?: unknown;
                    cshGroupIds?: unknown;
                };
            };
        };

        const payload = (errObj.response?.data && typeof errObj.response.data === 'object')
            ? errObj.response.data
            : errObj;

        const base = typeof payload.error === 'string' && payload.error.trim()
            ? payload.error.trim()
            : (typeof payload.message === 'string' && payload.message.trim() ? payload.message.trim() : '');

        const names = Array.isArray(payload.cshGroupNames)
            ? payload.cshGroupNames.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
            : [];

        const ids = Array.isArray(payload.cshGroupIds)
            ? payload.cshGroupIds
                .map((id) => Number(id))
                .filter((id) => !Number.isNaN(id) && id > 0)
            : [];

        if (base) {
            if (names.length > 0) {
                return `${base} CSH groups: ${names.join(', ')}`;
            }
            if (ids.length > 0) {
                return `${base} CSH group IDs: ${ids.join(', ')}`;
            }
            return base;
        }
    }

    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    return fallback;
};

export const BuildingForm: React.FC<BuildingFormProps> = ({
    onSubmit,
    initialData = {},
    isLoading = false,
    siteId,
    siteHierarchyId,
    existingRules = [],
    existingRuleDetailsFromParent,
    onExistingRuleDetailsChange,
    onDeleteRule,
    onActivateRule,
    onEditRule,
    editingRuleId,
    editingRuleData,
    onCancelEditRule,
    onEditingRuleDataChange,
    onSiteDeleted,
    id
}) => {

    const { errors, handleChangeFormField, isValid = false, validateAll, setValues, validateField } =
        useFormValidation<BuildingFormData>({
            initialValues: [{
                customerName: initialData.customerName || "",
                postCode: initialData.postCode || "",
                address1: initialData.address1 || "",
                address2: initialData.address2 || "",
                townCity: initialData.townCity || "",
                county: initialData.county || "",
                country: initialData.country || "",
            }],
            rules: {
                customerName: [
                    validators.required("Customer Name is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `Customer Name minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_CHAR_LENGTH, `Customer Name maximum length is ${MAX_CHAR_LENGTH} characters`),
                ],

                postCode: [
                    validators.required("Post Code is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `Post Code minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_POSTCODE_LENGTH, `Post Code maximum length is ${MAX_POSTCODE_LENGTH} characters`),
                    validators.pattern(/^[A-Za-z0-9 ]+$/, "Post Code can only contain letters, numbers and spaces")
                ],

                address1: [
                    validators.required("Address is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `Address minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_CHAR_LENGTH, `Address maximum length is ${MAX_CHAR_LENGTH} characters`),
                ],

                address2: [
                ],

                townCity: [
                    validators.required("Town/City is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `Town/City minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_CHAR_LENGTH, `Town/City maximum length is ${MAX_CHAR_LENGTH} characters`),
                ],

                county: [
                    validators.required("County is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `County minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_CHAR_LENGTH, `County maximum length is ${MAX_CHAR_LENGTH} characters`),
                ],

                country: [
                    validators.required("Country is required"),
                    validators.minLength(MIN_CHAR_LENGTH, `Country minimum length is ${MIN_CHAR_LENGTH} characters`),
                    validators.maxLength(MAX_CHAR_LENGTH, `Country maximum length is ${MAX_CHAR_LENGTH} characters`),
                ],
            }

        });

    const [formData, setFormData] = useState<BuildingFormData>({
        customerName: initialData.customerName || '',
        address1: initialData.address1 || '',
        address2: initialData.address2 || '',
        townCity: initialData.townCity || '',
        county: initialData.county || '',
        country: initialData.country || '',
        postCode: initialData.postCode || ''
    });

    // Consider this an edit mode
    const hasInitialData = Boolean(initialData && (
        initialData.customerName || initialData.postCode || initialData.address1 || initialData.townCity || initialData.country
    ));

    const isEditMode = Boolean((id && !id.endsWith('-new')) || hasInitialData);

    const [ruleData, setRuleData] = React.useState<RuleFormData[]>([]);
    const [editRuleFormData, setEditRuleFormData] = React.useState<RuleFormData[]>([]);
    const [existingRuleDetails, setExistingRuleDetails] = useState<Record<number, RuleDetail>>({});
    const existingRuleDetailsRef = React.useRef<Record<number, RuleDetail>>({});
    const [sites, setSites] = useState<SiteListItem[]>([]);
    const [sitesLoading, setSitesLoading] = useState<boolean>(false);
    const [postcodeLookupLoading, setPostcodeLookupLoading] = useState<boolean>(false);
    const [postcodeLookupError, setPostcodeLookupError] = useState<string | null>(null);
    const [suggestedAddresses, setSuggestedAddresses] = useState<AddressLookupResult[]>([]);
    const [showFullForm, setShowFullForm] = useState<boolean>(isEditMode);
    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const [ruleValidationError, setRuleValidationError] = useState<string | null>(null);

    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
    const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
    const [deletingSiteIds, setDeletingSiteIds] = useState<Set<string | number>>(new Set());
    const [siteDeleteMessage, setSiteDeleteMessage] = useState<string | null>(null);
    const [siteDeleteError, setSiteDeleteError] = useState<string | null>(null);
    const resolverRef = useRef<((value: boolean) => void) | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const siteDeleteMessageRef = useRef<HTMLDivElement>(null);

    const waitForModalConfirm = (): Promise<boolean> => {
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setOpenModalConfirm(true);
        });
    };

    const handleDeleteSite = async (site: SiteListItem) => {
        const displayName = site.siteName || site.displayName || site.siteIdentifier;
        setModalMessageText(`Are you sure you want to delete "${displayName}"?`);
        setModalHeadingText('Delete Site');
        setOpenModalConfirm(true);

        const confirmed = await waitForModalConfirm();

        if (!confirmed) {
            return;
        }

        const siteHierarchyId = site.siteHierarchyId;
        if (!siteHierarchyId) {
            setSiteDeleteError(`Failed to delete ${displayName}: Site Hierarchy ID not found`);
            return;
        }

        setDeletingSiteIds(prev => new Set(prev).add(siteHierarchyId));
        try {
            const result = await deleteSite(siteHierarchyId);
            if (result.success) {
                setSites(prev => prev.filter(s => s.id !== site.id));
                setSiteDeleteError(null);
                setSiteDeleteMessage(`Site "${displayName}" deleted successfully`);
                setTimeout(() => setSiteDeleteMessage(null), 4000);
                onSiteDeleted?.(displayName);
            } else {
                setSiteDeleteError(`Failed to delete ${displayName}: ${formatDeleteErrorMessage(result.error, 'Unknown error')}`);
            }
        } catch (err) {
            setSiteDeleteError(`Failed to delete ${displayName}: ${formatDeleteErrorMessage(err, 'Unknown error')}`);
        } finally {
            setDeletingSiteIds(prev => {
                const next = new Set(prev);
                next.delete(siteHierarchyId);
                return next;
            });
        }
    };

    // When editingRuleData changes, populate the edit form
    React.useEffect(() => {
        if (editingRuleData) {
            setEditRuleFormData([editingRuleData]);
        } else {
            setEditRuleFormData([]);
        }
    }, [editingRuleData]);

    // Merge parent's existingRuleDetails into local cache when it changes
    React.useEffect(() => {
        if (existingRuleDetailsFromParent) {
            setExistingRuleDetails(prev => {
                const updated = { ...prev, ...existingRuleDetailsFromParent };
                existingRuleDetailsRef.current = updated;
                return updated;
            });
        }
    }, [existingRuleDetailsFromParent]);

    React.useEffect(() => {
        existingRuleDetailsRef.current = existingRuleDetails;
    }, [existingRuleDetails]);

    // Load full details for existingRules so we can show group names
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!existingRules || existingRules.length === 0) {return;}
            try {
                const ids = existingRules.map(r => r.siteRuleId).filter(Boolean);
                const results = await Promise.allSettled(ids.map(ruleId => getRule(ruleId)));
                const map: Record<number, RuleDetail> = {};
                results.forEach((r, idx) => {
                    if (r.status === 'fulfilled') {
                        const value = r.value;
                        if (value?.success && value.data) { map[ids[idx]] = value.data; }
                    }
                });
                // Merge new details with existing cache (preserve cached details for rules that failed to fetch)
                if (mounted) {
                    const updated = { ...existingRuleDetailsRef.current, ...map };
                    existingRuleDetailsRef.current = updated;
                    setExistingRuleDetails(updated);
                    onExistingRuleDetailsChange?.(updated);
                }
            } catch (e) {
                console.info('[BuildingForm] load existing rule details failed', e);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [existingRules]);
    

    // Fetch sites when siteHierarchyId or siteId changes
    React.useEffect(() => {
        let isMounted = true;

        const findSiteAndCustomer = (nodes: SiteTreeNode[], parentCustomer?: SiteTreeNode): SiteTreeNode | undefined => {
            for (const node of nodes) {
                if (node.type === 'customer') {
                    const result = findSiteAndCustomer(node.children || [], node);
                    if (result) {return result;}
                } else if (node.type === 'site' && node.sirId === siteId) {
                    return parentCustomer;
                } else {
                    console.info(`[BuildingForm] Checking node ${node.title} (type: ${node.type}) for siteId ${siteId}`);
                }
            }
            return undefined;
        };

        const findCustomer = (nodes: SiteTreeNode[]): SiteTreeNode | undefined => {
            for (const node of nodes) {
                if (node.type === 'customer' && node.customerId === siteHierarchyId) {return node;}
                if (node.children) {
                    const found = findCustomer(node.children);
                    if (found) {return found;}
                }
            }
            return undefined;
        };

        const mapSitesFromCustomer = (customerNode: SiteTreeNode) => {
            const siteNodes = customerNode.children?.filter(n => n.type === 'site') || [];
            const mapped = siteNodes.map(node => ({
                id: node.sirId || 0,
                siteIdentifier: node.siteIdentifier || node.title,
                siteName: node.title,
                displayName: node.title,
                siteHierarchyId: node.shId || customerNode.shId,
                status: node.statusName || undefined,
                isOnline: node.isActive || false,
                isOffline: !node.isActive,
                approvedAt: undefined,
            } as SiteListItem));
            console.info('[BuildingForm] Mapped sites:', mapped);
            return mapped;
        };

        const fallbackFilterSites = async () => {
            console.info('[BuildingForm] Customer node not found in tree, falling back to listSites');
            const res = await listSites();
            if (!isMounted) {return;}
            if (!res?.success || !res.data) {
                setSites([]);
                return;
            }
            const allSites = res.data as SiteListItem[];
            let filtered = allSites;
            if (siteHierarchyId) {
                filtered = allSites.filter(s => s.siteHierarchyId === siteHierarchyId);
            } else if (siteId) {
                const current = allSites.find(s => s.id === Number(siteId));
                if (current && current.siteHierarchyId) {
                    filtered = allSites.filter(s => s.siteHierarchyId === current.siteHierarchyId);
                } else {
                    filtered = allSites.filter(s => s.id === Number(siteId));
                }
            } else {
                console.info('[BuildingForm] No siteHierarchyId or siteId provided, showing all sites');
            }
            setSites(filtered);
        };

        async function loadSites() {
            setSitesLoading(true);
            try {
                const treeRes = await listSiteTree();
                if (!isMounted) {return;}
                if (!treeRes?.success || !treeRes.data) {
                    setSites([]);
                    return;
                }

                const tree = treeRes.data as SiteTreeNode[];
                let customerNode: SiteTreeNode | undefined;

                if (siteId) {
                    customerNode = findSiteAndCustomer(tree);
                    console.info('[BuildingForm] Searching for customer via siteId:', siteId, 'Found:', customerNode?.title);
                } else if (siteHierarchyId) {
                    customerNode = findCustomer(tree);
                    console.info('[BuildingForm] Searching for customer with customerId:', siteHierarchyId, 'Found:', customerNode?.title);
                } else {
                    console.info('[BuildingForm] No siteId or siteHierarchyId provided, skipping customer search in tree');
                }

                if (customerNode && customerNode.children) {
                    setSites(mapSitesFromCustomer(customerNode));
                } else {
                    await fallbackFilterSites();
                }
            } catch (err) {
                console.info('[BuildingForm] loadSites error', err);
                setSites([]);
            } finally {
                if (isMounted) {setSitesLoading(false);}
            }
        }

        // Only attempt to load when editing an existing customer (siteHierarchyId or siteId present)
        if (siteHierarchyId || siteId) {
            loadSites();
        } else {
            setSites([]);
        }

        return () => { isMounted = false; };
    }, [siteHierarchyId, siteId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            return updated;
        });
        
        // If user enters a customer name, reveal the full form so they can continue without postcode
        if (name === 'customerName' && value && value.trim().length > 0) {
            setShowFullForm(true);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const valid = validateAll([formData]);

        let hasErrors = false;
        setRuleValidationError(null);

        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }
        else{
            console.info('Unexpected validation result:', valid);
        }

        if (ruleData && ruleData.length > 0) {
            const ruleValidationErrors: string[] = [];

            ruleData.forEach((rule, idx) => {
                if (!rule.ruleName || rule.ruleName.trim() === '') {
                    ruleValidationErrors.push(`Rule ${idx + 1}: Name is required`);
                }

                if (!siteId && (!rule.appliesToSiteIds || rule.appliesToSiteIds.trim() === '')) {
                    ruleValidationErrors.push(`Rule ${idx + 1}: At least one building must be selected`);
                }

                const hasNoPaths = !rule.filteringPaths || rule.filteringPaths.trim() === '';
                const hasNoPriorities = !rule.filteringPriorities || rule.filteringPriorities.length === 0;
                if (hasNoPaths && hasNoPriorities) {
                    ruleValidationErrors.push(`Rule ${idx + 1}: Either Path IDs or Priorities must be selected`);
                }
            });

            if (ruleValidationErrors.length > 0) {
                hasErrors = true;
                setRuleValidationError(ruleValidationErrors.join(' | '));
            }
        }

        if (hasErrors) {
            setShowErrorSummary(true);
            // Scroll form to top to show error summary
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        // If no rules provided, pass null for second arg
        onSubmit(formData, (ruleData && ruleData.length > 0) ? ruleData : null);
    };

    const handleCloseStatusMessage = () => {
        setShowErrorSummary(false);
        setRuleValidationError(null);
    };

    // Debug: log which siteId is being passed to RulesForm
    React.useEffect(() => {
        console.info('[BuildingForm] siteId prop changed:', { siteId, siteHierarchyId });
    }, [siteId, siteHierarchyId]);

    // Debounced postcode lookup: produce a list of suggested addresses when postcode is entered.
    // Suggestions are shown in a dropdown; choosing one will populate the address fields.
    React.useEffect(() => {
        let isMounted = true;
        let t: number | undefined;
        const pc = formData.postCode;
        // Only attempt lookup when postcode has some length (loose validation)
        if (pc && pc.trim().length >= 2) {
            setPostcodeLookupError(null);
            // debounce 600ms
            t = window.setTimeout(async () => {
                try {
                    setPostcodeLookupLoading(true);
            // Use addresses endpoint (may return multiple in future providers)
            const res = await lookupPostcodeAddresses(pc.trim());
                    if (!isMounted) {return;}
                    setSuggestedAddresses(res || []);
                } catch (err: unknown) {
                    console.info('[BuildingForm] postcode lookup failed', err);
                    if (!isMounted) {return;}
                    setPostcodeLookupError((err as Error)?.message || 'Lookup failed');
                    setSuggestedAddresses([]);
                } finally {
                    if (isMounted) {setPostcodeLookupLoading(false);}
                }
            }, TOAST_ERROR_MS);
        } else {
            setPostcodeLookupError(null);
            setPostcodeLookupLoading(false);
            setSuggestedAddresses([]);
        }
        return () => {
            isMounted = false;
            if (t) {clearTimeout(t);}
        };
    }, [formData.postCode]);

    React.useEffect(() => {
        if (siteDeleteMessage) {
            siteDeleteMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [siteDeleteMessage]);

    return (
        <div id={id} className="building-form">
            <Modal
                isOpen={openModalConfirm}
                title={modalHeadingText ?? ''}
                headerClass="modal__header--danger"
                content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
                onConfirm={() => {
                    resolverRef.current?.(true);
                    setOpenModalConfirm(false);
                }}
                onCancel={() => {
                    resolverRef.current?.(false);
                    setOpenModalConfirm(false);
                }}
            />
            <form ref={formRef} onSubmit={handleSubmit} className="form form--csh">

                <h3 className="form__title form__title--building">
                    {isEditMode ? 'Edit Customer' : 'Add Customer'}
                </h3>
    
                {showErrorSummary && (
                    [
                        ...new Set(
                            [
                                ...(ruleValidationError ? [ruleValidationError] : []),
                                ...errors.flatMap(err => Object.values(err).filter((x): x is string => Boolean(x)))
                            ]
                        )
                    ].length > 0 && (
                    <StatusMessage kind="error" onClose={handleCloseStatusMessage}>
                        <FormErrorSummary
                            errors={[
                                ...new Set(
                                    [
                                        ...(ruleValidationError ? [ruleValidationError] : []),
                                        ...errors.flatMap(err => Object.values(err).filter((x): x is string => Boolean(x)))
                                    ]
                                )
                            ]}
                        />
                    </StatusMessage>
                ))}

                <div className="form__group">
                    <FormField
                        label="Customer Name"
                        name="customerName"
                        type="text"
                        value={formData.customerName}
                        showErrorMessage={false}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "customerName")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.customerName}
                    />
                </div>

                <div className="form__group">
                    <FormField
                        label="Post Code"
                        name="postCode"
                        type="text"
                        value={formData.postCode}
                        showErrorMessage={false}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "postCode")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.postCode}
                    />

                    <div style={{ marginTop: 6 }}>
                        {postcodeLookupLoading && <small className="text-muted">Checking postcode…</small>}
                        {postcodeLookupError && <small className="text-danger">{postcodeLookupError}</small>}
                    </div>

                    {suggestedAddresses?.length > 0 && (
                        <div className="form__group mt-1">
                            <label htmlFor="addressSelector" className="form__label">Select address</label>
                            <select
                                id="addressSelector"
                                className="form__control"
                                onChange={e => {
                                    const idx = Number.parseInt(e.target.value, 10);
                                    if (Number.isNaN(idx)) {return;}

                                    const sel = suggestedAddresses[idx];
                                    if (!sel) {return;}

                                    setFormData(prev => ({
                                        ...prev,
                                        townCity: sel.townCity || prev.townCity,
                                        county: sel.county || prev.county,
                                        country: sel.country || prev.country,
                                        postCode: sel.postCode || prev.postCode
                                    }));                                   
                                    
                                    // Update validation values directly (avoid synthetic event casting)
                                    setValues(prev => {
                                        const next = [...prev];
                                        const current = (next[0] || {}) as BuildingFormData;
                                        next[0] = {
                                            ...current,
                                            townCity: sel.townCity || current.townCity,
                                            county: sel.county || current.county,
                                            country: sel.country || current.country,
                                            postCode: suggestedAddresses[idx].address1 || current.postCode,
                                        } as BuildingFormData;
                                        return next;
                                    });
                                    if (sel.townCity) { validateField(0, 'townCity', sel.townCity); }
                                    if (sel.county) { validateField(0, 'county', sel.county); }
                                    if (sel.country) { validateField(0, 'country', sel.country); }
                                    validateField(0, 'postCode', suggestedAddresses[idx].address1);
                                    setFormData(prev => ({ ...prev, postCode: suggestedAddresses[idx].address1 || '' }));
                                    setShowFullForm(true);
                                    setSuggestedAddresses([]);
                                }}>
                                <option value="">-- select suggested address --</option>
                                {suggestedAddresses.map((s, i) => (
                                    <option key={`${s.address1}-${s.townCity}-${s.county}`} value={String(i)}>
                                        {s.display ||
                                            [s.address1, s.townCity, s.county, s.country]
                                                .filter(Boolean)
                                                .join(', ')
                                        }
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {showFullForm && (
                    <div className="form__group">
                        <FormField
                            label="Address Line 1"
                            name="address1"
                            type="text"
                            value={formData.address1}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "address1")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.address1}
                        />
                    </div>
                )}

                <div className="form__group">
                    <FormField
                        label="Address Line 2"
                        name="address2"
                        type="text"
                        value={formData.address2}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "address2")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.address2}
                    />
                </div>

                <div className="form__row">
                    <div className="form__group">
                        <FormField
                            label="Town/City"
                            name="townCity"
                            type="text"
                            value={formData.townCity}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "townCity")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.townCity}
                        />
                    </div>

                    <div className="form__group">
                        <FormField
                            label="County"
                            name="county"
                            type="text"
                            value={formData.county}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "county")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.county}
                        />
                    </div>
                </div>

                {showFullForm && (
                    <div className="form__row">
                        <div className="form__group">
                            <FormField
                                label="Country"
                                name="country"
                                type="text"
                                value={formData.country}
                                disabled={isLoading}
                                showErrorMessage={false}
                                onChange={e => {
                                    handleChangeFormField(0, "country")(e);
                                    handleChange(e);
                                }}
                                error={errors[0]?.country}
                            />
                        </div>
                    </div>
                )}

                {(sitesLoading || (sites && sites.length > 0)) && (
                    <div className="form__section mt-2">
                        <h4 className="form__subtitle">Existing Sites</h4>
                        {siteDeleteError && (
                            <StatusMessage kind="error" onClose={() => setSiteDeleteError(null)}>
                                {siteDeleteError}
                            </StatusMessage>
                        )}
                        {siteDeleteMessage && <div ref={siteDeleteMessageRef} className="delete-success-message">{siteDeleteMessage}</div>}
                        {sitesLoading ? (
                            <div className="csh-sites-loader" role="status" aria-live="polite">
                                <span className="csh-sites-loader__spinner" aria-hidden="true" />
                                <span className="csh-sites-loader__text">Loading sites...</span>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Site Name</th>
                                            <th>Identifier</th>
                                            <th>Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sites.map(site => {
                                            const siteHierarchyId = site.siteHierarchyId;
                                            const isDeleting = siteHierarchyId ? deletingSiteIds.has(siteHierarchyId) : false;
                                            return (
                                            <tr key={site.id}>
                                                <td>{site.siteName || site.displayName || site.siteIdentifier}</td>
                                                <td>{site.siteIdentifier}</td>
                                                <td>{site.status || (site.isOnline ? 'Online' : site.isOffline ? 'Offline' : '')}</td>
                                                <td>
                                                    {isDeleting ? (
                                                        <div className="csh-sites-loader" role="status" aria-live="polite">
                                                            <span className="csh-sites-loader__spinner" aria-hidden="true" />
                                                            <span className="csh-sites-loader__text">Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn__icon btn--delete-clean"
                                                            onClick={() => { void handleDeleteSite(site); }}
                                                            disabled={deletingSiteIds.size > 0}
                                                            title="Delete site"
                                                            aria-label={`Delete ${site.siteName || site.displayName || site.siteIdentifier}`}
                                                        >
                                                            <span className="se-icon se-icon--delete" aria-hidden="true" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {existingRules.length > 0 && (
                    <div className="form__section mt-2">
                        <h4 className="form__subtitle">Existing Rules</h4>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Rule Name</th>
                                        <th>Status</th>
                                        <th>Distribution Groups</th>
                                        <th>Attachment Groups</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingRules.map(rule => (
                                        <tr key={rule.siteRuleId}>
                                            <td>{rule.ruleName}</td>
                                            <td>
                                                <span className={rule.isActive ? 'badge badge--active' : 'badge badge--inactive'}>
                                                    {rule.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{(() => {
                                                const d = existingRuleDetails?.[rule.siteRuleId]?.distributionGroups;
                                                if (Array.isArray(d) && d.length > 0) {return d.map(g => g.profileName ?? '').filter(Boolean).join(', ');} 
                                                return 'None';
                                            })()}</td>
                                            <td>{(() => {
                                                const a = existingRuleDetails?.[rule.siteRuleId]?.attachmentGroups;
                                                if (Array.isArray(a) && a.length > 0) {return a.map(g => g.profileName ?? '').filter(Boolean).join(', ');} 
                                                return 'None';
                                            })()}</td>
                                            <td>{formatDateTime(rule.createdAt)}</td>
                                                <td>
                                                            <ActionList
                                                        disabled={isLoading}
                                                        customActions={(() => {
                                                            const actions: ActionItem[] = [];
                                                            if (rule.isActive) {
                                                                // Show Deactivate first, then Edit
                                                                if (onDeleteRule) {actions.push({ label: 'Deactivate', onClick: () => onDeleteRule(rule.siteRuleId), className: 'actions__item--deactivate' });}
                                                                if (onEditRule) {actions.push({ label: 'Edit', onClick: () => onEditRule(rule.siteRuleId), className: 'actions__item--edit' });}
                                                            } else {
                                                                // Show Activate first, then Edit, then Delete
                                                                if (onActivateRule) {actions.push({ label: 'Activate', onClick: () => onActivateRule(rule.siteRuleId), className: 'actions__item--acknowledge' });}
                                                                if (onEditRule) {actions.push({ label: 'Edit', onClick: () => onEditRule(rule.siteRuleId), className: 'actions__item--edit' });}
                                                                if (onDeleteRule) {actions.push({ label: 'Delete', onClick: () => onDeleteRule(rule.siteRuleId), className: 'actions__item--delete' });}
                                                            }
                                                            return actions;
                                                        })()}
                                                    />
                                                </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {editingRuleId && editingRuleData && (
                    <div className="rule-edit-section mt-2 mb-2">
                        
                        <RulesForm 
                            value={editRuleFormData}
                            onChange={(d: RuleFormData[]) => {
                                setEditRuleFormData(d);
                                // Immediately notify parent when rule form changes
                                if (onEditingRuleDataChange && d.length > 0) {
                                    onEditingRuleDataChange(d[0]);
                                } else if (onEditingRuleDataChange) {
                                    onEditingRuleDataChange(null);
                                }
                                else {
                                    console.info('No onEditingRuleDataChange callback provided or data is empty');
                                }
                            }} 
                            disabled={isLoading} 
                            siteId={siteId}
                            editIndex={0}
                            onCancelEditRule={onCancelEditRule}
                        />
                    </div>
                )}

                {!editingRuleId && siteId && <RulesForm value={ruleData} onChange={(d: RuleFormData[]) => setRuleData(d)} disabled={isLoading} siteId={siteId} onCancelEditRule={onCancelEditRule} />}
            </form>
        </div>
    );
};
